import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { ConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useQueryParam } from '@plato/ui-legacy/lib/hooks/use-query-param';
import { stringParam } from '@plato/ui-legacy/lib/query-params';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@plato/ui/containers/dropdown-menu';
import { MenuItemLabel, MenuItemLeading } from '@plato/ui/primitives/menu';
import { EditIcon, EllipsisVerticalIcon, FileCheck, Trash2Icon } from 'lucide-react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { SalesDraftOrderListItem } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { DraftOrderCreateDialog } from '../../sales-draft-new/views/create-draft-order-dialog.tsx';

type DraftListActionDropdownProps = {
    draft: SalesDraftOrderListItem;
};

export function DraftListActionDropdown({ draft }: DraftListActionDropdownProps) {
    const [open, setOpen] = React.useState(false);
    const { t } = useTranslation('sales-draft');
    const navigate = useNavigate();
    const { toast } = useToast();
    const [, setDeletedDraftId] = useQueryParam('deleted_draft_id', stringParam());
    const { canReadQuoteOrderFromGrid } = useAppConfig();
    const deleteDraftMutation = api.platoUser.command.salesDraft.draft.delete.useMutation();

    const { draft_order_id } = draft;

    const handleDelete = async () => {
        await deleteDraftMutation.mutateAsync({
            draft_order_id,
        });

        toast({
            title: t('sales_draft__delete_success_toast__title'),
            description: (
                <Trans
                    t={t}
                    i18nKey="sales_draft__delete_success_toast__description"
                    components={{
                        linkTag: (
                            <div
                                className="text-blue-700 hover:underline"
                                onClick={() => {
                                    void DialogStateManager.show(DraftOrderCreateDialog, {
                                        source: 'drafts_list',
                                    });
                                }}
                            />
                        ),
                    }}
                />
            ),
            icon: 'success',
        });

        setDeletedDraftId(draft_order_id);
    };

    const handleConfirmDelete = () => {
        void DialogStateManager.show(ConfirmationDialog, {
            focus: 'destroy',
            icon: FileCheck,
            title: t('sales_draft__delete_confirmation_dialog__title'),
            description: t('sales_draft__delete_confirmation_dialog__description'),
            accept: {
                text: t('form__delete', {
                    ns: 'common',
                }),
                action: handleDelete,
            },
            reject: {
                text: t('form__cancel', {
                    ns: 'common',
                }),
            },
        });
    };

    if (!canReadQuoteOrderFromGrid && draft.state === 'SUBMITTED') {
        return null;
    }

    return (
        <div className="flex items-center justify-center">
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger render={<Button variant="icon" icon={EllipsisVerticalIcon} />} />
                <DropdownMenuContent align="end" side="bottom">
                    {draft.state === 'NEW' ? (
                        <>
                            <DropdownMenuItem
                                onClick={() => {
                                    void navigate(`/drafts/${encodeURIComponent(draft_order_id)}/line-items`);
                                }}
                            >
                                <MenuItemLeading className="text-inherit">
                                    <EditIcon />
                                </MenuItemLeading>
                                <MenuItemLabel>{t('sales_draft__list_view__action__edit', {})}</MenuItemLabel>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleConfirmDelete} className="text-danger">
                                <MenuItemLeading className="text-danger">
                                    <Trash2Icon />
                                </MenuItemLeading>
                                <MenuItemLabel>
                                    {t('sales_draft__list_view__action__delete', {})}
                                </MenuItemLabel>
                            </DropdownMenuItem>
                        </>
                    ) : (
                        <DropdownMenuItem
                            onClick={() => {
                                void navigate(
                                    draft.type === 'QUOTE'
                                        ? `/quotes/${encodeURIComponent(draft.sales_order_id ?? '')}`
                                        : `/orders/${encodeURIComponent(draft.sales_order_id ?? '')}`,
                                );
                            }}
                        >
                            <MenuItemLeading className="text-inherit">
                                <EditIcon />
                            </MenuItemLeading>
                            <MenuItemLabel>{t('sales_draft__list_view__action__view', {})}</MenuItemLabel>
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
